import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchMyOrgPositions, type ManagerOrganigramEditScope, type MeOrgPosition } from '~/composables/equipe/useMyOrgPositions'
import { useEffectiveGardianRoles } from '~/composables/auth/useEffectiveGardianRoles'
import { useMe } from '~/composables/auth/useMe'
import { canActivateOrganigrammeEditOnRoute } from '~~/utils/auth/organigrammeEditRouteGate'
import { isLegacyAdmin } from '~~/utils/auth/abilities'
import { useOrgStore } from '~~/stores/org'

/** Publié par le layout pour que les tuiles lisent le mode édition sans relancer `/v1/me`. */
const ORGANIGRAMME_IS_EDITING_KEY = 'organigramme-edit-route-is-editing'

async function loadMyOrgPositionsIntoSharedState(
  myOrgPositions: { value: MeOrgPosition[] },
  myManagerOrganigramEditScope: { value: ManagerOrganigramEditScope | null },
): Promise<void> {
  try {
    const payload = await fetchMyOrgPositions()
    myOrgPositions.value = payload.positions
    myManagerOrganigramEditScope.value = payload.managerOrganigramEditScope
  } catch {
    myOrgPositions.value = []
    myManagerOrganigramEditScope.value = null
  }
}

function distinctDirLibsFromPositions(positions: MeOrgPosition[]): string[] {
  const seen = new Set<string>()
  const dirLibs: string[] = []
  for (const position of positions) {
    const dirLib = String(position.dirLib ?? '').trim()
    if (dirLib === '') {
      continue
    }
    const key = dirLib.toUpperCase()
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    dirLibs.push(dirLib)
  }
  return dirLibs
}

/**
 * Droits « mode édition organigramme » sur la route courante (aligné sur `layouts/default.vue`).
 * État positions / scope partagé via `useState` pour cohérence entre layout et pages.
 */
export function useOrganigrammeEditRouteAccess() {
  const route = useRoute()
  const { loggedIn } = useOidcAuth()
  const { me } = useMe()
  const { roles } = useEffectiveGardianRoles()

  const myOrgPositions = useState<MeOrgPosition[]>('organigramme-edit-route-me-positions', () => [])
  const myManagerOrganigramEditScope = useState<ManagerOrganigramEditScope | null>(
    'organigramme-edit-route-manager-scope',
    () => null,
  )

  const directionAdminDirLibs = computed(function computeDirectionAdminDirLibs(): string[] {
    const fromMe = Array.isArray(me.value?.directionAdminDirLibs) ? me.value.directionAdminDirLibs : []
    if (fromMe.length > 0) {
      return fromMe
    }
    if (!isLegacyAdmin(roles.value)) {
      return fromMe
    }
    return distinctDirLibsFromPositions(myOrgPositions.value)
  })

  watch(
    () => loggedIn.value,
    function onOrganigrammeEditLoggedInChange(isLoggedIn) {
      if (isLoggedIn && import.meta.client) {
        void loadMyOrgPositionsIntoSharedState(myOrgPositions, myManagerOrganigramEditScope)
      } else {
        myOrgPositions.value = []
        myManagerOrganigramEditScope.value = null
      }
    },
    { immediate: true },
  )

  const canEditOrganigramOnCurrentView = computed(function computeCanEditOrganigramOnCurrentView(): boolean {
    if (!loggedIn.value) {
      return false
    }
    return canActivateOrganigrammeEditOnRoute(
      route.path,
      route,
      myOrgPositions.value,
      roles.value,
      {
        directionAdminDirLibs: directionAdminDirLibs.value,
        managerOrganigramEditScope: myManagerOrganigramEditScope.value,
      },
    )
  })

  const store = useOrgStore()
  const isOrganigrammeEditing = computed(
    () => store.isEditing && canEditOrganigramOnCurrentView.value,
  )
  const publishedIsOrganigrammeEditing = useState(ORGANIGRAMME_IS_EDITING_KEY, () => false)
  watch(isOrganigrammeEditing, (active) => {
    publishedIsOrganigrammeEditing.value = active
  }, { immediate: true })

  return {
    canEditOrganigramOnCurrentView,
    isOrganigrammeEditing,
    myOrgPositions,
    myManagerOrganigramEditScope,
    directionAdminDirLibs,
  }
}

/** Mode édition affiché : store allumé et droit de modifier la vue courante. */
export function useIsOrganigrammeEditing() {
  const publishedIsOrganigrammeEditing = useState(ORGANIGRAMME_IS_EDITING_KEY, () => false)
  return computed(() => publishedIsOrganigrammeEditing.value)
}
